<ng-container *transloco="let t">
  <div id="help-faq-page" class="relative min-h-screen bg-ld-neutral-50" [class.help-faq--mobile]="isMobile()">
    <div class="max-w-page mx-auto py-6 page-content-padding">
      <h1 id="help-faq-title" class="text-page-title mb-6">{{ t('commonHelpFaqs') }}</h1>

      <div class="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
        <div id="help-faq-main-content">
          <div id="help-faq-container" class="flex flex-col card-shell">
            <div id="help-faq-search-section" class="px-4 md:px-10 py-6 border-b border-ld-border">
              <div class="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
                <h2 id="help-faq-section-title" class="text-section-heading">
                  {{ t('commonHelpFaqs') }}
                </h2>

                <div id="help-faq-search-wrapper" class="relative w-full md:w-70">
                  <div class="flex items-center gap-2 px-5 py-2 border-2 border-ld-primary rounded-[20px]">
                    <mat-icon class="text-ld-primary w-4! h-4! text-[16px]!">search</mat-icon>
                    <input
                      id="help-faq-search-input"
                      type="text"
                      class="flex-1 bg-transparent border-none outline-none text-ld-primary font-dm font-bold text-base uppercase tracking-[0.5px] placeholder:text-ld-primary"
                      [placeholder]="t('commonFieldsSearch')"
                      [ngModel]="searchText()"
                      (ngModelChange)="onSearchChange($event)"
                    />
                  </div>
                </div>
              </div>
              @if (searchText()) {
                <p id="help-faq-search-results" class="text-body text-ld-neutral-500 mt-3">
                  {{ t('helpFaqSearchResults', { count: visibleQuestionCount(), query: searchText() }) }}
                </p>
              }
            </div>

            @if (isLoading()) {
              <div id="help-faq-loading" class="p-12 flex items-center justify-center">
                <div class="flex flex-col items-center gap-4">
                  <mat-spinner [diameter]="48"></mat-spinner>
                  <p class="text-body text-ld-neutral-500">{{ t('commonLabelsLoading') }}</p>
                </div>
              </div>
            }

            @if (error()) {
              <div
                id="help-faq-error"
                class="m-6 bg-red-50 border border-red-200 rounded-2xl p-6 flex items-start gap-3"
              >
                <mat-icon class="text-red-500 w-6! h-6! text-[24px]! shrink-0">error</mat-icon>
                <div>
                  <p class="text-card-title text-red-700">{{ t('helpFaqErrorTitle') }}</p>
                  <p id="help-faq-error-text" class="text-body text-red-600">{{ error() }}</p>
                </div>
              </div>
            }

            @if (!isLoading() && !error()) {
              <div id="help-faq-content" class="px-4 md:px-10 py-6">
                @if (filteredCategories().length === 0 && searchText()) {
                  <div id="help-faq-no-results" class="py-12 text-center">
                    <div class="w-16 h-16 bg-ld-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <mat-icon class="w-8! h-8! text-[32px]! text-ld-neutral-500">search_off</mat-icon>
                    </div>
                    <p class="font-dm text-lg text-ld-neutral-500">
                      {{ t('helpFaqNoResults', { query: searchText() }) }}
                    </p>
                    <p class="text-body text-ld-neutral-400 mt-2">{{ t('commonTryDifferentSearch') }}</p>
                  </div>
                } @else {
                  @for (category of filteredCategories(); track category.faqType; let catIndex = $index) {
                    <section [id]="'faq-category-' + catIndex" class="mb-8 last:mb-0">
                      <h2
                        [id]="'faq-category-title-' + catIndex"
                        class="text-section-heading text-ld-neutral mb-4 pb-3 border-b border-ld-border"
                      >
                        {{ category.faqType }}
                      </h2>

                      <mat-accordion [id]="'faq-accordion-' + catIndex" class="faq-accordion" multi>
                        @for (question of category.faqQuestionAnswers; track question.heading; let qIndex = $index) {
                          <mat-expansion-panel
                            [id]="toSlug(question.heading)"
                            [expanded]="expandedSlug() === toSlug(question.heading)"
                            class="bg-ld-neutral-50! rounded-xl! shadow-none! mb-3! last:mb-0!"
                          >
                            <mat-expansion-panel-header class="px-5! py-4! h-auto! min-h-14!">
                              <mat-panel-title class="text-card-title text-ld-neutral mr-4!">
                                {{ question.heading }}
                              </mat-panel-title>
                            </mat-expansion-panel-header>

                            <div
                              [id]="'faq-answer-' + catIndex + '-' + qIndex"
                              class="faq-answer-body px-5 pb-5 text-body text-ld-neutral-500 leading-relaxed"
                            >
                              <div [innerHTML]="question.detailPart"></div>
                              @if (question.detailPart2) {
                                <div class="mt-3" [innerHTML]="question.detailPart2"></div>
                              }
                              @if (question.detailPart3) {
                                <div class="mt-3" [innerHTML]="question.detailPart3"></div>
                              }
                            </div>
                          </mat-expansion-panel>
                        }
                      </mat-accordion>
                    </section>
                  }
                }
              </div>
            }
          </div>
        </div>

        <aside id="help-faq-sidebar" class="flex flex-col gap-6">
          <div id="help-faq-contact-card" class="card-base">
            <div id="help-faq-contact-header" class="flex items-center gap-3 mb-4">
              <ld-svg-icon
                id="help-faq-contact-icon"
                [src]="supportIcon"
                [attr.aria-label]="t('contactCardIconAlt')"
                class="w-8 h-8"
              />
              <h3 id="help-faq-contact-card-title" class="text-card-title">
                {{ t('contactCardTitle') }}
              </h3>
            </div>

            <p id="help-faq-contact-description" class="font-dm text-sm text-ld-neutral-700 leading-5 mb-4">
              {{ t('contactCardDescription') }}
            </p>

            <div id="help-faq-contact-hours" class="font-dm text-sm text-ld-neutral-700 leading-5 mb-5">
              <p class="font-semibold">{{ t('contactCardHoursTitle') }}</p>
              <p>{{ t('contactCardWeekdayHours') }} | {{ t('contactCardSaturdayHours') }}</p>
            </div>

            <div id="help-faq-contact-links" class="flex flex-col gap-3">
              <a
                id="help-faq-contact-link-chat"
                href="javascript:void(0)"
                (click)="openChat()"
                class="flex items-center gap-2 text-ld-primary font-dm text-sm font-medium cursor-pointer [&>span]:hover:underline"
              >
                <mat-icon class="text-lg!">chat</mat-icon>
                <span>{{ t('helpCardLetsChat') }}</span>
              </a>
              <a
                id="help-faq-contact-link-message"
                href="javascript:void(0)"
                (click)="openMessageCenter()"
                class="flex items-center gap-2 text-ld-primary font-dm text-sm font-medium cursor-pointer [&>span]:hover:underline"
              >
                <mat-icon class="text-lg!">email</mat-icon>
                <span>{{ t('contactCardMessageLink') }}</span>
              </a>
              <a
                id="help-faq-contact-link-phone"
                [href]="customerServiceTel()"
                class="flex items-center gap-2 text-ld-primary font-dm text-sm font-medium cursor-pointer [&>span]:hover:underline"
              >
                <mat-icon class="text-lg!">phone</mat-icon>
                <span>{{ t('contactCardCallLink', { phone: customerServicePhone() }) }}</span>
              </a>
            </div>

            <div
              id="help-faq-contact-share-screen"
              class="mt-5 flex flex-col gap-4 border-t border-ld-neutral-200 pt-5"
            >
              <p id="help-faq-contact-share-screen-prompt" class="font-dm text-sm text-ld-neutral-700 leading-5">
                <span id="help-faq-contact-share-screen-prompt-title" class="block font-semibold text-ld-neutral-900">
                  {{ t('shareScreenCardStandardPromptTitle') }}
                </span>
                <span id="help-faq-contact-share-screen-prompt-description" class="block">
                  {{ t('shareScreenCardStandardPromptDescription') }}
                </span>
              </p>

              <button
                id="help-faq-contact-share-screen-button"
                data-testid="help-faq-share-screen-button"
                type="button"
                class="inline-flex w-full items-center justify-center gap-2 rounded-[20px] bg-ld-lime px-5 py-2 font-dm text-base font-bold uppercase leading-6 tracking-[0.5px] text-ld-neutral-700 transition-colors cursor-pointer hover:bg-ld-lime-hover focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2 disabled:cursor-not-allowed"
                [disabled]="isShareScreenLoading()"
                [class.opacity-60]="isShareScreenLoading()"
                [class.pointer-events-none]="isShareScreenLoading()"
                (click)="onShareScreenClick()"
              >
                @if (isShareScreenLoading()) {
                  <mat-spinner id="help-faq-contact-share-screen-spinner" diameter="16" class="inline-block" />
                } @else {
                  <ld-svg-icon
                    id="help-faq-contact-share-screen-button-icon"
                    [src]="screenshareIcon"
                    aria-hidden="true"
                    class="size-4 shrink-0 text-ld-neutral-700"
                  />
                }
                <span id="help-faq-contact-share-screen-button-label">{{ t('shareScreenCardButton') }}</span>
              </button>
            </div>
          </div>
        </aside>
      </div>

      <section id="help-faq-get-in-touch" class="mt-10 py-10 border-t border-ld-border text-center">
        <h2 id="help-faq-get-in-touch-title" class="text-xl font-dm font-bold text-ld-neutral mb-3">
          {{ t('helpFaqGetInTouchTitle') }}
        </h2>
        <p id="help-faq-get-in-touch-description" class="text-base text-ld-neutral-500 font-dm mb-4">
          {{ t('helpFaqGetInTouchDescriptionPrefix') }}
          <a
            [href]="customerServiceTel()"
            class="text-ld-link font-medium hover:underline cursor-pointer whitespace-nowrap"
          >
            {{ customerServicePhone() }}
          </a>
          {{ t('helpFaqGetInTouchDescriptionSuffix') }}
        </p>
        <a
          id="help-faq-get-in-touch-link"
          [routerLink]="contactUsLink()"
          class="inline-flex items-center gap-1 text-ld-link font-dm font-medium hover:underline cursor-pointer"
        >
          {{ t('helpFaqVisitContactUs') }}
          <mat-icon class="w-5! h-5! text-[20px]!" aria-hidden="true">arrow_forward</mat-icon>
        </a>
      </section>
    </div>
  </div>
</ng-container>
